import React, { useEffect, useMemo, useState } from 'react';

import amplitude from 'amplitude-js';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Text } from 'apps/auth/components/common/Text/Text';
import { InputSearch } from 'apps/auth/components/form/components/InputSearch';
import { getParamsForGlobalMethods, getRecipientTypeParam } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { COUNTRY_AND_METHOD_SCREEN_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { GLOBAL_METHODS, RECIPIENT_TYPE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import FullCountryList from './FullCountryList';
import { FullViewCountrySearch } from './FullViewCountrySearch';
import GlobalSources from './GlobalSources';

export const SelectCountryScreenPureComponent = (props) => {
    const { globalSources, searchCountryName, sendFlowV3, fromCountry } = useExtra();
    const [isFullViewSearch, toggleIsFullViewSearch] = useState(!!searchCountryName);
    const { sendMoreOptions } = usePageMessages();

    useEffect(() => {
        amplitude.getInstance().logEvent(COUNTRY_AND_METHOD_SCREEN_SHOWN, {
            [GLOBAL_METHODS]: getParamsForGlobalMethods(globalSources),
            [RECIPIENT_TYPE]: getRecipientTypeParam(props.recipientType),
        });
    }, []);

    const getSortedCountries = (countries) => {
        if (countries.length > 0) {
            const paymentCountry = countries?.find((country) => country.code === fromCountry.code);
            const userCountry = countries?.find((country) => country.code === props.userCountry.code);
            const filteredCountries = countries.filter((country) => country.code !== paymentCountry.code && country.code !== userCountry.code);
            if (paymentCountry.code === userCountry.code) {
                return [paymentCountry, ...filteredCountries];
            } else {
                return [paymentCountry, userCountry, ...filteredCountries];
            }
        }
    };

    const countriesTo = useMemo(() => {
        if (props.quantityCountriesForRender > 0 && Array.isArray(props.countries)) {
            let suggestedCountries = props.countries.filter((uc) => uc?.usageCount > 0).sort((ua, ub) => ub.usageCount - ua.usageCount);
            const popularRankCountries = props.countries.filter((pc) => pc?.popularRank > 0).sort((pa, pb) => pa.popularRank - pb.popularRank);
            suggestedCountries.forEach((sc) => {
                if (popularRankCountries.includes(sc)) {
                    popularRankCountries.splice(popularRankCountries.indexOf(sc), 1);
                }
            });
            suggestedCountries = suggestedCountries.concat(popularRankCountries);
            suggestedCountries = suggestedCountries.slice(0, props.quantityCountriesForRender);
            const otherCountry = props.countries.filter((oc) => -1 === suggestedCountries.findIndex((sc) => sc.id === oc.id));
            suggestedCountries = suggestedCountries.concat(otherCountry);
            return suggestedCountries;
        } else if (props.hasCustomSorting && fromCountry && props.userCountry) {
            return getSortedCountries(props.countries);
        }
        return props.countries;
    }, [props.countries]);

    const getSelectedCountry = (selectedCountry) => {
        return props.hasCustomSorting ? fromCountry.code === selectedCountry.code : null;
    };

    const toggleSearch = (e, show) => {
        e.preventDefault();
        toggleIsFullViewSearch(show);
    };

    const sendUrIPrefix = props.sendUrIPrefix;
    const fullViewSearchBackUri = props.fullViewSearchBackUri;

    return (
        <>
            {isFullViewSearch ? (
                <FullViewCountrySearch
                    countries={props.countries}
                    userCountry={props.userCountry}
                    title={props.title}
                    countriesHeader={props.countriesHeader}
                    placeholder={props.fullScreenSearchPlaceholder}
                    gotIt={props.gotIt}
                    notificationText={props.notificationText}
                    recipientType={props.recipientType}
                    searchNoResult={props.searchNoResult}
                    sendUrIPrefix={sendUrIPrefix}
                    sendUriPostfix={props.sendUriPostfix}
                    toggleSearch={toggleSearch}
                    onCountryItemClick={props.onCountryItemClick}
                    backUrl={fullViewSearchBackUri}
                    ccyField={props.ccyField}
                    showCurrency={props.showCurrency}
                    getSelectedCountry={getSelectedCountry}
                    sendBrazeEvent={props.sendBrazeEvent}
                />
            ) : (
                <ModalComponent
                    isIconClose={!!props.hideRecipientList}
                    canCloseModal={props.canCloseModal}
                    linkTo={props.hideRecipientList ? props.linkTo : null}
                    onCloseIconClick={() => {
                        if (!props.hideRecipientList) {
                            props.toggleRecipientList?.(true);
                        }
                        props.closeModal?.();
                    }}
                >
                    <HeaderPanel
                        title={props.title}
                        indentSmall={sendFlowV3 && globalSources?.length > 0}
                        indentCompact={sendFlowV3 && globalSources?.length === 0}
                    />
                    <GlobalSources
                        recipientType={props.recipientType}
                        sendGlobalUrIPrefix={props.sendGlobalUrIPrefix}
                        sendUriPostfix={props.sendUriPostfix}
                        popupRef={props.popupRef}
                    />
                    {globalSources?.length > 0 &&
                        (sendFlowV3 ? (
                            <Separator isSubheader marginBottom="32px" marginTop="16px" leftText={sendMoreOptions} />
                        ) : (
                            <Text variant="div" color="secondary" size="subheader" indent="adaptive-32-48">
                                {sendMoreOptions}
                            </Text>
                        ))}
                    <InputSearch placeholder={props.searchPlaceholder} toggle={toggleSearch} />
                    <FullCountryList
                        countries={countriesTo}
                        gotIt={props.gotIt}
                        userCountry={props.userCountry}
                        notificationText={props.notificationText}
                        recipientType={props.recipientType}
                        sendUrIPrefix={sendUrIPrefix}
                        sendUriPostfix={props.sendUriPostfix}
                        onCountryItemClick={props.onCountryItemClick}
                        ccyField={props.ccyField}
                        countryWithCode={props.countryWithCode}
                        hideLetter
                        showCurrency={props.showCurrency}
                        getSelectedCountry={getSelectedCountry}
                        sendBrazeEvent={props.sendBrazeEvent}
                    />
                </ModalComponent>
            )}
        </>
    );
};
